import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VerifiedIcon from "@mui/icons-material/Verified";

import { GetStudyPlanUsageCall } from "../../redux/api/studyPlan.api";
import { IgListSkeleton } from "components/InstagramLoader";

const GOAL_LABEL = {
  exam: "Exam",
  mastery: "Mastery",
  general: "General",
};

function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function planDetail(row) {
  return [
    GOAL_LABEL[row.goal] || row.goal,
    row.exam_name,
    row.level,
    row.learn_with_ai ? "AI" : null,
    row.material_name,
  ]
    .filter(Boolean)
    .join(" · ");
}

function StudyPlanCard({ row, onOpen }) {
  const name = row.user?.name || "Unknown";
  const detail = planDetail(row);
  const progress = Math.max(0, Math.min(100, Number(row.progress) || 0));

  return (
    <Paper
      elevation={0}
      onClick={() => onOpen?.(row)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen?.(row);
        }
      }}
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        cursor: "pointer",
        transition: "border-color .15s ease, box-shadow .15s ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: "0 8px 22px rgba(37, 99, 235, 0.12)",
        },
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar src={row.user?.profile_image || undefined} sx={{ width: 36, height: 36 }}>
            {initials(name)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {name}
              </Typography>
              {row.user?.profile_verified ? (
                <VerifiedIcon sx={{ fontSize: 16, color: "info.main" }} />
              ) : null}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", wordBreak: "break-all" }}
            >
              {[row.user?.email, row.user?.university].filter(Boolean).join(" · ") || "—"}
            </Typography>
          </Box>
          <Chip size="small" color="primary" variant="outlined" label={`${progress}%`} />
        </Stack>

        <Box>
          <Typography variant="body2" fontWeight={700}>
            {row.subject || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {detail || row.status}
            {` · ${row.topics_count || 0} topics · ${row.lessons_completed || 0}/${row.lessons_count || 0} lessons`}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 6, borderRadius: 999 }}
        />

        <Typography variant="caption" color="text.secondary">
          Created {formatWhen(row.created_at)}
          {" · "}
          Last used {formatWhen(row.last_activity_at || row.updated_at)}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function AdminStatsStudyPlans() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total_plans: 0, total_users: 0 });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  const openPlan = (row) => {
    if (!row?.id) return;
    navigate(`/copilot-usage/study/${row.id}`);
  };

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    GetStudyPlanUsageCall({ page: page + 1, limit, search: search || undefined }).then((res) => {
      if (cancelled) return;
      if (res?.status === 200 && res.data) {
        setRows(Array.isArray(res.data.items) ? res.data.items : []);
        setTotal(res.data.pagination?.total || 0);
        setSummary({
          total_plans: res.data.summary?.total_plans || 0,
          total_users: res.data.summary?.total_users || 0,
        });
      } else {
        setRows([]);
        setTotal(0);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [page, limit, search, refreshTick]);

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" color="primary" variant="outlined" label={`${summary.total_plans} plans`} />
          <Chip size="small" variant="outlined" label={`${summary.total_users} people`} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            label="Search"
            placeholder="Name, email, or subject"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            fullWidth={isMobile}
            sx={{ minWidth: { xs: 0, sm: 220 }, flex: { xs: 1, sm: "none" } }}
          />
          <IconButton
            size="small"
            onClick={() => setRefreshTick((value) => value + 1)}
            aria-label="Refresh study plan usage"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {loading ? (
        <IgListSkeleton rows={5} rowHeight={56} />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
          No study plans yet.
        </Typography>
      ) : isMobile ? (
        <Stack spacing={1.25}>
          {rows.map((row) => (
            <StudyPlanCard key={row.id} row={row} onOpen={openPlan} />
          ))}
        </Stack>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Profile</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell align="right">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const name = row.user?.name || "Unknown";
                const detail = planDetail(row);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => openPlan(row)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar
                          src={row.user?.profile_image || undefined}
                          sx={{ width: 34, height: 34 }}
                        >
                          {initials(name)}
                        </Avatar>
                        <Box>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" color="primary" label={name} />
                            {row.user?.profile_verified ? (
                              <VerifiedIcon sx={{ fontSize: 16, color: "info.main" }} />
                            ) : null}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {[row.user?.email, row.user?.university].filter(Boolean).join(" · ") ||
                              "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {row.subject || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {detail || row.status}
                        {` · ${row.topics_count} topics · ${row.lessons_completed}/${row.lessons_count} lessons`}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.progress || 0}%</TableCell>
                    <TableCell>{formatWhen(row.created_at)}</TableCell>
                    <TableCell>{formatWhen(row.last_activity_at || row.updated_at)}</TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      <Tooltip title="View plan details">
                        <IconButton size="small" onClick={() => openPlan(row)} aria-label="View plan">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={limit}
        onRowsPerPageChange={(event) => {
          setLimit(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage={isMobile ? "Per page" : "Rows per page"}
        sx={{
          "& .MuiTablePagination-toolbar": {
            flexWrap: "wrap",
            gap: 1,
            px: { xs: 0, sm: 2 },
          },
        }}
      />
    </Box>
  );
}
